import CodeMirror from 'codemirror'
import React, { useEffect, useRef } from 'react'
import {
    applyCustomFieldDecorations,
    consumeCustomFieldToken,
} from '../features/custom-fields/editor-language'
import type { CustomFieldEditorContext } from '../features/custom-fields/types'
import { addSyntaxV1, MERCHJAR_V1_MODE } from './actions2/syntax_v1'
import { addSyntaxV2, MERCHJAR_V2_MODE } from './actions2/syntax_v2'

interface SyntaxHighlightedCodeProps {
    code: string
    version?: number
    className?: string
    customFieldContext?: CustomFieldEditorContext
}

export const SyntaxHighlightedCode: React.FC<SyntaxHighlightedCodeProps> = ({
    code,
    version = 2,
    className = '',
    customFieldContext,
}) => {
    const editorRef = useRef<HTMLDivElement>(null)
    const cmInstanceRef = useRef<CodeMirror.Editor | null>(null)

    useEffect(() => {
        if (!editorRef.current) return

        addSyntaxV1()
        addSyntaxV2()
        const mode =
            version === 1 ? MERCHJAR_V1_MODE : { name: MERCHJAR_V2_MODE, customFieldContext }
        const cmInstance = CodeMirror(editorRef.current, {
            value: code,
            mode,
            theme: 'merchjar',
            readOnly: true,
            lineNumbers: false,
            viewportMargin: Infinity,
        })
        const clearDecorations =
            version === 2
                ? applyCustomFieldDecorations(cmInstance, customFieldContext)
                : () => undefined
        cmInstanceRef.current = cmInstance

        return () => {
            clearDecorations()
            if (cmInstanceRef.current) {
                editorRef.current?.removeChild(editorRef.current.firstChild as Node)
                cmInstanceRef.current = null
            }
        }
    }, [code, version, customFieldContext])

    return <div ref={editorRef} className={`syntax-highlighted-code ${className}`} />
}

/** Exported for a direct editable/read-only token-recognition parity test. */
export const readOnlyCustomFieldToken = consumeCustomFieldToken
